/**
 * QuickFilters Component
 * Predefined filter suggestions for common use cases
 */

import React from 'react';
import { Zap, User, TrendingUp, TrendingDown, CheckCircle, AlertCircle, Clock, Building2, Users, Calendar } from 'lucide-react';
import type { OKRFilters, FilterMetadata } from '../../types/okr';
import { useAuth } from '../../context/AuthContext';

interface QuickFilter {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  filters: Partial<OKRFilters>;
}

interface QuickFiltersProps {
  onApplyQuickFilter: (filters: Partial<OKRFilters>) => void;
  metadata?: FilterMetadata | null;
}

export const QuickFilters: React.FC<QuickFiltersProps> = ({ onApplyQuickFilter, metadata }) => {
  const { user } = useAuth();

  // Find the active cycle from metadata
  const activeCycle = metadata?.cycles?.find((c) => c.isActive);

  const quickFilters: QuickFilter[] = [
    {
      id: 'my-okrs',
      label: 'My OKRs',
      icon: <User className="w-4 h-4" />,
      description: 'OKRs owned by me',
      filters: {
        ownerIds: user ? [user.id] : [],
      },
    },
    {
      id: 'at-risk',
      label: 'At Risk',
      icon: <AlertCircle className="w-4 h-4" />,
      description: 'Progress below 30%',
      filters: {
        progressMin: 0,
        progressMax: 30,
      },
    },
    {
      id: 'on-track',
      label: 'On Track',
      icon: <TrendingUp className="w-4 h-4" />,
      description: 'Progress between 50-80%',
      filters: {
        progressMin: 50,
        progressMax: 80,
      },
    },
    {
      id: 'ahead',
      label: 'Ahead',
      icon: <CheckCircle className="w-4 h-4" />,
      description: 'Progress above 80%',
      filters: {
        progressMin: 80,
        progressMax: 100,
      },
    },
    {
      id: 'behind',
      label: 'Behind',
      icon: <TrendingDown className="w-4 h-4" />,
      description: 'Progress below 50%',
      filters: {
        progressMin: 0,
        progressMax: 50,
      },
    },
    {
      id: 'recently-updated',
      label: 'Recently Updated',
      icon: <Clock className="w-4 h-4" />,
      description: 'Updated in last 7 days',
      filters: {
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      },
    },
    {
      id: 'company-okrs',
      label: 'Company OKRs',
      icon: <Building2 className="w-4 h-4" />,
      description: 'Company-level objectives',
      filters: {
        types: ['company'],
      },
    },
    {
      id: 'team-okrs',
      label: 'Team OKRs',
      icon: <Users className="w-4 h-4" />,
      description: 'Team objectives',
      filters: {
        types: ['team'],
      },
    },
    // Active Cycle quick filter (only shown if there's an active cycle)
    ...(activeCycle
      ? [
          {
            id: 'active-cycle',
            label: 'Active Cycle',
            icon: <Calendar className="w-4 h-4" />,
            description: activeCycle.name,
            filters: {
              cycleIds: [activeCycle.id],
            },
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-primary-600">
        <Zap className="w-4 h-4" />
        <h3 className="text-sm font-semibold">Quick Filters</h3>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {quickFilters.map((quickFilter) => (
          <button
            key={quickFilter.id}
            onClick={() => onApplyQuickFilter(quickFilter.filters)}
            className="flex items-start gap-2 p-3 text-left bg-white border border-stone-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
            title={quickFilter.description}
          >
            <div className="mt-0.5 text-stone-600 group-hover:text-primary-600 transition-colors">
              {quickFilter.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-stone-900 group-hover:text-primary-700">
                {quickFilter.label}
              </div>
              <div className="text-xs text-stone-500 mt-0.5 truncate">
                {quickFilter.description}
              </div>
            </div>
          </button>
        ))}
      </div>

      <p className="text-xs text-stone-500 italic">
        Click any quick filter to instantly apply common filter patterns
      </p>
    </div>
  );
};
